import { Kyb, SearchNormalOutline, Table } from '@aslanpay/portal-common-lib';
import {
  Center,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Spacer,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useDisclosure,
} from '@chakra-ui/react';
import { PaginationState, SortingState } from '@tanstack/react-table';
import { defaultPageSize } from 'app/constants/table';
import useDebounce from 'app/hooks/useDebounce';
import useFetchKybPage from 'app/hooks/useFetchKybPage/useFetchKybPage';
import getKybColumns from 'app/utils/table/kyb';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { KybActions } from '../UI/Table';

import KybApproveModal from './KybApproveModal';
import KybDeclineModal from './KybDeclineModal';
import KybDetailsModal from './KybDetailsModal';

function KybTab() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [selectedKyb, setSelectedKyb] = useState<Kyb | null>(null);
  const currentPage = searchParams.get('page') ? Number(searchParams.get('page')) - 1 : 0;

  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('query') || '');

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: currentPage,
    pageSize: defaultPageSize,
  });

  const {
    isOpen: isKybDetailsModalOpen,
    onOpen: onOpenKybDetailsModal,
    onClose: onCloseKybDetailsModal,
  } = useDisclosure();

  const {
    isOpen: isKybApproveModalOpen,
    onOpen: onOpenKybApproveModal,
    onClose: onCloseKybApproveModal,
  } = useDisclosure();

  const {
    isOpen: isKybDeclineModalOpen,
    onOpen: onOpenKybDeclineModal,
    onClose: onCloseKybDeclineModal,
  } = useDisclosure();

  const resetPagination = () => {
    setPagination({ pageIndex: 0, pageSize: defaultPageSize });
  };

  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);
    resetPagination();
  };

  const onSearchQueryChangedDebounced = useDebounce(handleSearchQueryChange);

  const [sorting, setSorting] = useState<SortingState>(() => [
    ...(searchParams.get('sortBy')
      ? [
          {
            id: searchParams.get('sortBy') as string,
            desc: searchParams.get('sort') === 'DESC',
          },
        ]
      : []),
  ]);

  // it helps to avoid fetching all tabs data on initial load and tab change
  // it also help to show the number of items on the awaiting approval tab
  const { data: pendingKybs, isLoading: isLoadingPendingKyb } = useFetchKybPage({
    query: searchQuery,
    status: ['AWAITING_APPROVAL'],
    sortBy: sorting[0]?.id || 'createdAt',
    sort: !sorting[0] || sorting[0]?.desc ? 'DESC' : 'ASC',
    page: pageIndex,
    size: pageSize,
  });

  const { data: approvedKybs, isLoading: isLoadingApprovedKyb } = useFetchKybPage({
    query: searchQuery,
    status: ['APPROVED'],
    sortBy: sorting[0]?.id || 'approvedAt',
    sort: !sorting[0] || sorting[0]?.desc ? 'DESC' : 'ASC',
    page: pageIndex,
    size: pageSize,
  });

  const { data: declinedKybs, isLoading: isLoadingDeclinedKyb } = useFetchKybPage({
    query: searchQuery,
    status: ['DECLINED'],
    sortBy: sorting[0]?.id || 'declinedAt',
    sort: !sorting[0] || sorting[0]?.desc ? 'DESC' : 'ASC',
    page: pageIndex,
    size: pageSize,
  });

  const viewKyb = (kyb: Kyb) => {
    setSelectedKyb(kyb);
    onOpenKybDetailsModal();
  };

  const approveKyb = (kyb: Kyb) => {
    setSelectedKyb(kyb);
    onOpenKybApproveModal();
  };

  const declineKyb = (kyb: Kyb) => {
    setSelectedKyb(kyb);
    onOpenKybDeclineModal();
  };

  const actions: KybActions = {
    view: viewKyb,
    approve: approveKyb,
    decline: declineKyb,
  };

  useEffect(() => {
    setSearchParams(
      {
        page: `${pageIndex + 1}`,
        ...(searchQuery && { query: searchQuery }),
        ...(sorting[0] && { sortBy: sorting[0]?.id, sort: sorting[0]?.desc ? 'DESC' : 'ASC' }),
      },
      { replace: true }
    );
  }, [pageIndex, searchQuery, sorting, setSearchParams]);

  return (
    <Flex flexDir="column" flexGrow={1} gap="20px">
      {selectedKyb && (
        <KybDetailsModal
          isOpen={isKybDetailsModalOpen}
          onClose={onCloseKybDetailsModal}
          kyb={selectedKyb}
        />
      )}
      {selectedKyb && (
        <KybApproveModal
          kybId={selectedKyb?.id || ''}
          isOpen={isKybApproveModalOpen}
          onClose={onCloseKybApproveModal}
        />
      )}
      {selectedKyb && (
        <KybDeclineModal
          kybId={selectedKyb?.id || ''}
          isOpen={isKybDeclineModalOpen}
          onClose={onCloseKybDeclineModal}
        />
      )}
      <Tabs variant="unstyled" onChange={(index) => setActiveTabIndex(index)}>
        <TabList>
          <Tab
            borderBottom="1px solid #E2E8F0"
            _selected={{
              color: 'brand.purple',
              borderBottom: '2px solid',
              borderColor: 'brand.purple',
            }}
            px={4}
            py={2}
            fontWeight="medium"
          >
            Awaiting approval ({pendingKybs?.total || 0})
          </Tab>
          <Tab
            borderBottom="1px solid #E2E8F0"
            _selected={{
              color: 'brand.purple',
              borderBottom: '2px solid',
              borderColor: 'brand.purple',
            }}
            px={4}
            py={2}
            fontWeight="medium"
          >
            Approved
          </Tab>
          <Tab
            borderBottom="1px solid #E2E8F0"
            _selected={{
              color: 'brand.purple',
              borderBottom: '2px solid',
              borderColor: 'brand.purple',
            }}
            px={4}
            py={2}
            fontWeight="medium"
          >
            Declined
          </Tab>
          <Spacer />
          <InputGroup
            maxW="344px"
            minW="344px"
            aria-disabled={!searchQuery.length && !pendingKybs?.list.length}
            color="brand.darkGray"
            _disabled={{ color: 'system.disabled' }}
          >
            <Input
              isDisabled={!searchQuery.length && !pendingKybs?.list.length}
              onChange={(event) => onSearchQueryChangedDebounced(event.target.value)}
              defaultValue={searchQuery}
              type="text"
              placeholder="Search"
            />
            <InputRightElement w="56px" h="full">
              <SearchNormalOutline boxSize="24px" />
            </InputRightElement>
          </InputGroup>
        </TabList>

        <TabPanels mt={4}>
          <TabPanel p={0}>
            {isLoadingPendingKyb ? (
              <Center width="full" height="full" flexGrow={1}>
                <Spinner color="brand.purple" size="xl" mb="50px" />
              </Center>
            ) : (
              <Table
                columns={getKybColumns('pending', actions)}
                data={pendingKybs?.list || []}
                total={pendingKybs?.total || 0}
                pageNumber={pendingKybs?.pageNumber || pageIndex}
                pageSize={pendingKybs?.pageSize || pageSize}
                onPaginationChange={setPagination}
                sorting={sorting}
                onSortingChange={setSorting}
              />
            )}
          </TabPanel>

          <TabPanel p={0}>
            {activeTabIndex !== 1 && isLoadingApprovedKyb ? (
              <Center width="full" height="full" flexGrow={1}>
                <Spinner color="brand.purple" size="xl" mb="50px" />
              </Center>
            ) : (
              <Table
                columns={getKybColumns('approved', actions)}
                data={approvedKybs?.list || []}
                total={approvedKybs?.total || 0}
                pageNumber={approvedKybs?.pageNumber || pageIndex}
                pageSize={approvedKybs?.pageSize || pageSize}
                onPaginationChange={setPagination}
                sorting={sorting}
                onSortingChange={setSorting}
              />
            )}
          </TabPanel>

          <TabPanel p={0}>
            {activeTabIndex !== 2 && isLoadingDeclinedKyb ? (
              <Center width="full" height="full" flexGrow={1}>
                <Spinner color="brand.purple" size="xl" mb="50px" />
              </Center>
            ) : (
              <Table
                columns={getKybColumns('declined', actions)}
                data={declinedKybs?.list || []}
                total={declinedKybs?.total || 0}
                pageNumber={declinedKybs?.pageNumber || pageIndex}
                pageSize={declinedKybs?.pageSize || pageSize}
                onPaginationChange={setPagination}
                sorting={sorting}
                onSortingChange={setSorting}
              />
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
  );
}

export default KybTab;
